import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

import config from '../config';
import { ContextProvider } from '../ContextProvider';
import MapProvider from '../components/SearchMap/MapProvider';
import { getClientData } from '../util/clientData';
import { applyDateLocale } from '../util/dateLocale';
import { logError } from '../util/log';
import { isCordova } from '../util/mobile/deviceDetection';

// react-intl's getMessageFormat does not cache thrown errors, so without
// dedup the same broken message id would get reported to Sentry on every
// render. LocaleLayout also renders server-side, where this module lives
// across many requests, so the dedup set is capped rather than left to grow
// forever — once it's full we just clear it, which is fine for a
// noise-suppression feature like this.
const REPORTED_INTL_ERRORS_CAP = 500;
const reportedIntlErrors = new Set();

// Hard cap on Sentry reports per process, independent of dedup, so a burst
// of many distinct broken ids can't flood Sentry either.
const MAX_INTL_SENTRY_REPORTS = 20;
let intlSentryReportCount = 0;

/**
 * onError handler for IntlProvider. MISSING_TRANSLATION is expected noise
 * (untranslated/removed keys) and only surfaced as a dev console warning.
 * FORMAT_ERROR and any other/unknown code — including future "uncompiled
 * message" errors from the AST precompile migration — are real bugs and get
 * reported to Sentry, deduped and rate-limited. Must never throw itself,
 * since react-intl calls this from inside render.
 *
 * @param {import('react-intl').IntlError} err Error reported by react-intl
 * @param {string} locale Locale that was active when the error occurred
 */
const handleIntlError = (err, locale) => {
  if (err?.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[intl] Missing translation "${err?.descriptor?.id}" for locale "${locale}"`);
    }
    return;
  }

  try {
    // logError relies on browser-only globals (e.g. window.currentAppVersion)
    // during Sentry init, so it's not safe to call during SSR.
    if (typeof window === 'undefined') return;

    const key = `${locale}:${err?.descriptor?.id}`;
    if (reportedIntlErrors.has(key) || intlSentryReportCount >= MAX_INTL_SENTRY_REPORTS) {
      return;
    }
    if (reportedIntlErrors.size >= REPORTED_INTL_ERRORS_CAP) {
      reportedIntlErrors.clear();
    }
    reportedIntlErrors.add(key);
    intlSentryReportCount += 1;

    logError(err, 'intl-error', { messageId: err?.descriptor?.id, locale });
  } catch (e) {
    // onError must never throw — swallow any failure in the reporting path.
  }
};

async function loadMessages(locale) {
  switch (locale) {
    case 'de':
      return import(/* webpackChunkName: "messages_de" */'../translations/compiled/de.json');
    case 'en':
      return import(/* webpackChunkName: "messages_en" */'../translations/compiled/en.json');
    case 'es':
      return import(/* webpackChunkName: "messages_es" */'../translations/compiled/es.json');
    case 'fr':
      return import(/* webpackChunkName: "messages_fr" */'../translations/compiled/fr.json');
    case 'it':
      return import(/* webpackChunkName: "messages_it" */'../translations/compiled/it.json');
    case 'no':
      return import(/* webpackChunkName: "messages_no" */'../translations/compiled/no.json');
    case 'sv':
      return import(/* webpackChunkName: "messages_sv" */'../translations/compiled/sv.json');
    default:
      return import(/* webpackChunkName: "messages_en" */'../translations/compiled/en.json');
  }
}

const IntlProviderWrapperComponent = ({ children, selectedLocale, serverMessages = null }) => {
  const getLocale = (locale) => locale && locale.split('-')[0];
  const getSupportedLocaleJson = (locale) =>
    config.languages[getLocale(locale)].replace('.json', '');

  const supportedLocale = getSupportedLocaleJson(selectedLocale);

  const [messages, setMessages] = useState(serverMessages);
  const [loadedLocale, setLoadedLocale] = useState(serverMessages ? supportedLocale : null);

  useEffect(() => {
    if (loadedLocale !== supportedLocale) {
      let cancelled = false;
      (async () => {
        try {
          const msgs = await loadMessages(supportedLocale);
          if (!cancelled) {
            setMessages(msgs.default || msgs);
            setLoadedLocale(supportedLocale);
            // Force browser to recalculate scroll area after locale content changes.
            // Without this, the viewport retains the scroll extent of the previous
            // (taller) page, leaving whitespace below shorter content.
            requestAnimationFrame(() => {
              document.documentElement.style.overflow = 'hidden';
              void document.documentElement.offsetHeight;
              document.documentElement.style.overflow = '';
              window.scrollTo(0, 0);
            });
          }
        } catch (e) {
          // Chunk load failure — keep showing current messages rather than crashing
          if (!cancelled) {
            console.error('Failed to load messages for locale:', supportedLocale, e);
          }
        }
      })();
      return () => { cancelled = true; };
    }
  }, [supportedLocale, loadedLocale]);

  // Use loadedLocale for IntlProvider to keep locale and messages in sync.
  // While new messages are loading, render with the previous locale's messages
  // to avoid format errors from mismatched variable names across translations.
  const activeLocale = loadedLocale || supportedLocale;

  // Keep moment/dayjs on the locale the messages render in. app.js resolves it
  // once at mount, but the URL locale can still change client-side without a
  // reload — the locale-redirect loader and Cordova's stored-locale redirect
  // both navigate in-app — and dates would otherwise keep the mount-time locale.
  useEffect(() => {
    applyDateLocale(activeLocale);
  }, [activeLocale]);

  // IntlProvider shallow-compares its config to decide whether to rebuild the
  // intl object it puts on context. An inline arrow is a new reference on every
  // render, which would rebuild that object every time and re-render every
  // consumer of the intl context, so keep the identity stable per locale.
  // NOTE: declared before the `messages === null` early return — hooks must run
  // unconditionally.
  const onIntlError = useCallback((err) => handleIntlError(err, activeLocale), [activeLocale]);

  if (messages === null) return null;

  return (
    <IntlProvider locale={activeLocale} messages={messages} onError={onIntlError}>
      {children}
    </IntlProvider>
  );
};

IntlProviderWrapperComponent.displayName = 'IntlProviderWrapper';

const IntlProviderWrapper = memo(IntlProviderWrapperComponent);

const LocaleLayoutComponent = () => {
  const { locale: pathLocale } = useParams();
  const location = useLocation();
  const context = useContext(ContextProvider);
  const { pathname, search } = location;

  const getLocale = (locale) => locale && locale.split('-')[0];
  const isSupported = (locale) => !!config.languages[getLocale(locale)];

  const getCordovaLocale = () => isCordova() && getClientData('vansite.locale');

  if (getCordovaLocale() && getCordovaLocale() !== pathLocale) {
    return <Navigate replace to={`/${getCordovaLocale()}${pathname}${search}`} />;
  }

  const browserLocale =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    window.navigator.language &&
    getLocale(navigator.language);

  const contextLocale =
    context &&
    context.locale &&
    (Array.isArray(context.locale)
      ? getLocale(context.locale[0])
      : getLocale(context.locale));
  if (context && context.locale && process.env.NODE_ENV === 'development') {
    console.log('context.locale', context.locale);
  }

  // Unsupported / missing-prefix locales are redirected by the `:locale` route
  // loader (createLocaleGuardLoader in dataRouter.js) so the redirect happens
  // server-side as a real 302 instead of rendering <Navigate> on the initial
  // StaticRouter pass. By the time this component renders, pathLocale is valid.

  const selectedLocale =
    (isSupported(pathLocale) && pathLocale) ||
    (isSupported(browserLocale) && browserLocale) ||
    (isSupported(contextLocale) && contextLocale) ||
    config.fallbackLocale;

  return (
    <IntlProviderWrapper selectedLocale={selectedLocale} serverMessages={context?.messages}>
      <MapProvider>
        <Outlet />
      </MapProvider>
    </IntlProviderWrapper>
  );
};

LocaleLayoutComponent.displayName = 'LocaleLayout';

const LocaleLayout = memo(LocaleLayoutComponent);

export default LocaleLayout;
